import UserContextStore from "components/Admin/Organizations/OrganizationDetail/OrganizationClientBrain/UserContextStore";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";
import type {
    ClassifierStatus,
    ClassifierStatusGroups,
} from "models/ClientBrain/ClassifierStatus";
import type {
    ClientType,
    HierarchyRow,
    OrganizationContext,
    TherapeuticAreaDto,
} from "models/ClientBrain/OrganizationContext";
import type { ClientBrainUserSummary } from "models/ClientBrain/UserContext";
import type {
    GlossaryEntry,
    GlossaryEntryPayload,
} from "models/ClientBrain/GlossaryEntry";
import ClientBrainService from "services/ClientBrainService";
import type {
    ProgramEventPayload,
    UpdateClassifierDescriptionPayload,
    UpdateHierarchyPayload,
} from "services/ClientBrainService";
import { OrganizationService } from "services/OrganizationService";
import { BaseStore } from "stores/BaseStore";
import { AcxStore } from "./RootStore";
import type { IRootStore } from "./RootStore";

const EMPTY_CONTEXT: OrganizationContext = { programEvents: [] };

function normalizeOrgContext(
    context: OrganizationContext | null | undefined,
): OrganizationContext {
    if (!context) return { programEvents: [] };
    return { ...context, programEvents: context.programEvents ?? [] };
}

function normalizeClassifierGroups(
    groups: Record<string, ClassifierStatus[] | ClassifierStatus> | null | undefined,
): ClassifierStatusGroups {
    const result: ClassifierStatusGroups = {};
    Object.entries(groups ?? {}).forEach(([key, value]) => {
        result[key] = Array.isArray(value) ? value : [value];
    });
    return result;
}

/**
 * Singleton store for the Client Brain page. Owns org-level context, the read-only hierarchy
 * rows, the "Add context" eligible-user list, and a Map of per-user UserContextStore instances
 * (Signals-style composition — see SignalsPageStore).
 */
@AcxStore
export class ClientBrainStore extends BaseStore {
    private readonly service = new ClientBrainService();
    private readonly organizationService = new OrganizationService();

    @observable organizationContext: OrganizationContext = EMPTY_CONTEXT;
    @observable userContextStores: Map<string, UserContextStore> = new Map();
    @observable eligibleUsers: ClientBrainUserSummary[] = [];
    @observable hierarchies: HierarchyRow[] = [];
    @observable therapeuticAreaOptions: TherapeuticAreaDto[] = [];
    @observable classifierGroups: ClassifierStatusGroups = {};
    @observable glossaryEntries: GlossaryEntry[] = [];
    @observable isLoading = false;
    @observable selectedUserId?: string;

    constructor(private rootStore: IRootStore) {
        super("ClientBrainStore");
        makeObservable(this);
    }

    @computed
    get userProfiles(): UserContextStore[] {
        return Array.from(this.userContextStores.values());
    }

    @computed
    get selectedUserStore(): UserContextStore | undefined {
        return this.selectedUserId
            ? this.userContextStores.get(this.selectedUserId)
            : undefined;
    }

    @action
    setSelectedUser(userId: string) {
        this.selectedUserId = userId;
    }

    @action
    async loadAll(_orgId: string, canEdit = true) {
        this.isLoading = true;
        try {
            const [
                orgContext,
                profiles,
                eligible,
                hierarchies,
                therapeuticAreaOptions,
                classifiers,
                glossaryEntries,
            ] = await Promise.all([
                this.service.getOrganizationContext(),
                this.service.getUserContexts(),
                canEdit ? this.service.getEligibleUsers() : Promise.resolve([]),
                this.service.getHierarchies(),
                canEdit
                    ? this.organizationService.getTherapeuticAreas()
                    : Promise.resolve([]),
                // Isolate classifier failures so they can't break the whole page.
                this.service.getClassifiers().catch(() => ({})),
                this.service.getGlossary(),
            ]);
            runInAction(() => {
                this.organizationContext = normalizeOrgContext(orgContext);
                const messageStore = this.rootStore.getStore(MessageStore);
                this.userContextStores = new Map(
                    profiles.map((p) => [
                        p.userId,
                        new UserContextStore(
                            p,
                            messageStore,
                            (userId, payload) =>
                                this.service.updateUserContext(
                                    userId,
                                    payload,
                                ),
                        ),
                    ]),
                );
                this.eligibleUsers = eligible;
                this.hierarchies = hierarchies;
                this.therapeuticAreaOptions = therapeuticAreaOptions;
                this.classifierGroups = normalizeClassifierGroups(classifiers);
                this.glossaryEntries = glossaryEntries;
                this.selectedUserId = profiles[0]?.userId;
            });
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    @action
    resetState() {
        this.userContextStores = new Map();
        this.eligibleUsers = [];
        this.hierarchies = [];
        this.therapeuticAreaOptions = [];
        this.classifierGroups = {};
        this.glossaryEntries = [];
        this.organizationContext = EMPTY_CONTEXT;
        this.selectedUserId = undefined;
    }

    // --- Organization context (each section saves immediately) ---

    @action
    async setClientType(clientType: ClientType) {
        const updated = await this.service.setClientType(clientType);
        runInAction(() => {
            this.organizationContext = normalizeOrgContext(updated);
        });
    }

    @action
    async saveGoals(value: string) {
        const updated = await this.service.setGoals(value);
        runInAction(() => {
            this.organizationContext = normalizeOrgContext(updated);
        });
    }

    @action
    async addEvent(payload: ProgramEventPayload) {
        const updated = await this.service.addProgramEvent(payload);
        runInAction(() => {
            this.organizationContext = normalizeOrgContext(updated);
        });
    }

    @action
    async editEvent(id: string, payload: ProgramEventPayload) {
        const updated = await this.service.editProgramEvent(id, payload);
        runInAction(() => {
            this.organizationContext = normalizeOrgContext(updated);
        });
    }

    @action
    async deleteEvent(id: string) {
        const updated = await this.service.deleteProgramEvent(id);
        runInAction(() => {
            this.organizationContext = normalizeOrgContext(updated);
        });
    }

    @action
    async updateHierarchyDescription(id: string, description: string) {
        const updated = await this.service.updateHierarchyDescription(
            id,
            description,
        );
        runInAction(() => {
            this.hierarchies = this.hierarchies.map((hierarchy) =>
                hierarchy.id === updated.id ? updated : hierarchy,
            );
        });
    }

    @action
    async updateHierarchyTherapeuticAreas(
        id: string,
        therapeuticAreas: TherapeuticAreaDto[],
    ) {
        const updated = await this.service.updateHierarchyTherapeuticAreas(
            id,
            therapeuticAreas.map((area) => area.id),
        );
        runInAction(() => {
            this.hierarchies = this.hierarchies.map((hierarchy) =>
                hierarchy.id === updated.id ? updated : hierarchy,
            );
        });
    }

    @action
    async updateHierarchies(updates: UpdateHierarchyPayload[]) {
        const updated = await this.service.updateHierarchies(updates);
        runInAction(() => {
            this.hierarchies = updated;
        });
    }

    @action
    async updateClassifierDescriptions(
        updates: UpdateClassifierDescriptionPayload[],
    ) {
        const updated = await this.service.updateClassifierDescriptions(updates);
        runInAction(() => {
            this.classifierGroups = normalizeClassifierGroups(updated);
        });
    }

    @action
    async saveGlossary(entries: GlossaryEntryPayload[]) {
        const updated = await this.service.saveGlossary(entries);
        runInAction(() => {
            this.glossaryEntries = updated;
        });
        return updated;
    }

    // --- User context (on-demand) ---

    @action
    async addUserContext(userId: string) {
        const profile = await this.service.addUserContext(userId);
        runInAction(() => {
            this.userContextStores.set(
                profile.userId,
                new UserContextStore(
                    profile,
                    this.rootStore.getStore(MessageStore),
                    (id, payload) => this.service.updateUserContext(id, payload),
                ),
            );
            this.eligibleUsers = this.eligibleUsers.filter(
                (u) => u.userId !== profile.userId,
            );
            this.selectedUserId = profile.userId;
        });
    }

    @action
    async removeUserContext(userId: string) {
        const store = this.userContextStores.get(userId);
        await this.service.deleteUserContext(userId);
        runInAction(() => {
            this.userContextStores.delete(userId);
            if (store) {
                this.eligibleUsers = [
                    ...this.eligibleUsers,
                    {
                        userId: store.userId,
                        firstName: store.firstName,
                        lastName: store.lastName,
                        email: store.email,
                    },
                ];
            }
            if (this.selectedUserId === userId) {
                this.selectedUserId = this.userProfiles[0]?.userId;
            }
        });
    }
}

export default ClientBrainStore;
